<template>
  <div>
    <div v-if="loading" class="flex justify-center items-center min-h-[200px]">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-custom-yellow"></div>
    </div>
    <template v-else>
      <div class="relative overflow-x-auto shadow-md sm:rounded-lg mt-5">
        <table class="w-full text-left rtl:text-right text-gray-700">
          <thead>
            <tr class="bg-gray-100">
              <th scope="col" class="p-4">
                <div class="flex items-center">
                  <input id="checkbox-all-search" type="checkbox"
                    class="w-4 h-4 text-custom-yellow bg-gray-100 border-gray-300 rounded focus:ring-custom-yellow focus:ring-2" />
                  <label for="checkbox-all-search" class="sr-only">checkbox</label>
                </div>
              </th>
              <th scope="col" class="px-6">
                <div class="flex items-center">Name</div>
              </th>
              <th scope="col" class="px-6">
                <div class="flex items-center">Email</div>
              </th>
              <th scope="col" class="px-6">
                <div class="flex items-center">Phone</div>
              </th>
              <th scope="col" class="px-6">
                <div class="flex items-center">ID Number</div>
              </th>
              <th scope="col" class="px-6">
                <div class="flex items-center">Facility</div>
              </th>
              <th scope="col" class="px-6">
                <div class="flex items-center">Department</div>
              </th>
              <th scope="col" class="px-6">
                <div class="flex items-center">Status</div>
              </th>
              <th scope="col" class="px-6">
                <div class="flex items-center">Action</div>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(user, index) in filteredUsers" :key="index" class="border-b">
              <td class="w-4 p-4">
                <div class="flex items-center">
                  <input :id="'checkbox-table-search-' + index" type="checkbox" class="w-4 h-4" />
                  <label :for="'checkbox-table-search-' + index" class="sr-only">checkbox</label>
                </div>
              </td>
              <td class="px-6 py-1">{{ user.first_name }} {{ user.last_name }}</td>
              <td class="px-6 py-1">{{ user.email }}</td>
              <td class="px-6 py-1">{{ user.phone_number }}</td>
              <td class="px-6 py-1">{{ user.id_number }}</td>
              <td class="px-6 py-1">{{ user.user_roles?.[0]?.facility_department?.facility?.name ?? '' }}</td>
              <td class="px-6 py-1">{{ user.user_roles?.[0]?.facility_department?.department?.name ?? '' }}</td>
              <td class="px-6 py-1">{{ user.account_status }}</td>
              <td class="px-6 py-2">
                <button
                  @click="openCpdModal(user)"
                  class="inline-flex items-center gap-2 bg-custom-yellow hover:bg-yellow-500 text-white text-sm font-medium px-3 py-1 rounded">
                  <svg class="w-4 h-4" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                    <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v18m9-9H3"/>
                  </svg>
                  CPD Progress
                </button>
              </td>
            </tr>
            <tr v-if="filteredUsers.length === 0">
              <td colspan="8" class="px-6 py-4 text-center text-gray-500">No users found.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- CPD Progress Modal -->
      <div v-if="showCpdModal" class="fixed inset-0 z-50 flex items-center justify-center">
        <div class="absolute inset-0 bg-black/40 backdrop-blur-sm md:backdrop-blur" @click="closeCpdModal"></div>
        <div class="relative bg-white w-full max-w-5xl mx-4 rounded-lg shadow-lg max-h-[85vh] overflow-y-auto">
          <div class="flex items-center justify-between px-6 py-4 border-b">
            <div>
              <h3 class="text-lg font-semibold text-gray-800">CPD Progress</h3>
              <p class="text-sm text-gray-500" v-if="selectedUser">{{ selectedUser.first_name }} {{ selectedUser.last_name }} · {{ selectedUser.email }}</p>
            </div>
            <button class="text-gray-500 hover:text-gray-700" @click="closeCpdModal" aria-label="Close">
              <svg class="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div class="p-6">
            <div v-if="cpdLoading" class="flex justify-center items-center min-h-[160px]">
              <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-custom-yellow"></div>
            </div>

            <template v-else>
              <div v-if="cpdError" class="bg-red-50 text-red-700 border border-red-200 rounded px-4 py-2 mb-4">{{ cpdError }}</div>

              <div v-if="cpdProgress" class="space-y-6">
                <!-- Summary cards with color + Deadline -->
                <div class="grid grid-cols-1 md:grid-cols-5 gap-4">
                  <div class="rounded p-4 text-center bg-blue-50 border border-blue-100">
                    <div class="text-blue-600 text-xs">Target</div>
                    <div class="text-xl font-semibold text-blue-800">{{ cpdProgress.target }}</div>
                  </div>
                  <div class="rounded p-4 text-center bg-green-50 border border-green-100">
                    <div class="text-green-600 text-xs">Earned</div>
                    <div class="text-xl font-semibold text-green-800">{{ cpdProgress.earned }}</div>
                  </div>
                  <div class="rounded p-4 text-center bg-amber-50 border border-amber-100">
                    <div class="text-amber-600 text-xs">Remaining</div>
                    <div class="text-xl font-semibold text-amber-800">{{ cpdProgress.remaining }}</div>
                  </div>
                  <div class="rounded p-4 text-center bg-purple-50 border border-purple-100">
                    <div class="text-purple-600 text-xs">Completed Assessments</div>
                    <div class="text-xl font-semibold text-purple-800">{{ cpdProgress.completed_assessments_count }}</div>
                  </div>
                  <div class="rounded p-4 text-center bg-slate-50 border border-slate-100">
                    <div class="text-slate-600 text-xs">CPD Deadline</div>
                    <div class="text-xl font-semibold text-slate-800">{{ formatDate(cpdProgress.cpd_deadline) }}</div>
                  </div>
                </div>

                <!-- Progress bar -->
                <div>
                  <div class="flex items-center justify-between mb-1">
                    <span class="text-sm text-gray-700 font-medium">Progress</span>
                    <span class="text-sm text-gray-600">{{ cpdProgress.progress_percentage }}%</span>
                  </div>
                  <div class="w-full bg-gray-200 rounded h-3 overflow-hidden">
                    <div class="h-3 bg-custom-yellow" :style="{ width: (Number(cpdProgress.progress_percentage) || 0) + '%' }"></div>
                  </div>
                  <div class="mt-2 text-xs" :class="cpdProgress.is_target_met ? 'text-green-600' : 'text-gray-500'">
                    {{ cpdProgress.is_target_met ? 'Target met' : 'Target not yet met' }}
                  </div>
                </div>

                <!-- Assessments list -->
                <div>
                  <div class="flex items-center justify-between mb-2">
                    <h4 class="text-sm font-semibold text-gray-800">Completed Assessments</h4>
                  </div>
                  <div class="relative overflow-x-auto shadow-sm border border-gray-100 rounded">
                    <table class="w-full text-left text-gray-700">
                      <thead>
                        <tr class="bg-gray-50">
                          <th class="px-4 py-2 text-xs text-gray-500">Assessment</th>
                          <th class="px-4 py-2 text-xs text-gray-500">Score</th>
                          <th class="px-4 py-2 text-xs text-gray-500">CPD Points</th>
                          <th class="px-4 py-2 text-xs text-gray-500">Completed At</th>
                          <th class="px-4 py-2 text-xs text-gray-500">Certificate</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(ass, i) in cpdProgress.assessments" :key="i" class="border-t">
                          <td class="px-4 py-2">{{ ass.assessment_name }}</td>
                          <td class="px-4 py-2">{{ ass.score }}</td>
                          <td class="px-4 py-2">{{ ass.cpd_points }}</td>
                          <td class="px-4 py-2">{{ formatDateTime(ass.completed_at) }}</td>
                          <td class="px-4 py-2">{{ ass.certificate_serial_number }}</td>
                        </tr>
                        <tr v-if="!cpdProgress.assessments || cpdProgress.assessments.length === 0">
                          <td colspan="4" class="px-4 py-3 text-center text-gray-500">No assessments yet.</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </template>
          </div>

          <div class="px-6 py-4 border-t flex justify-end">
            <button @click="closeCpdModal" class="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50">Close</button>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import api from "@/services/api";

export default {
  name: "SupervisorStaffTab",
  props: {
    searchQuery: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      users: [],
      loading: false,
      error: null,
      filters: {},
      showCpdModal: false,
      selectedUser: null,
      cpdLoading: false,
      cpdError: null,
      cpdProgress: null,
    };
  },
  created() {
    this.fetchUsers();
  },
  computed: {
    filteredUsers() {
      const search = this.searchQuery.toLowerCase();
      const safeToLower = (value) => (typeof value === "string" ? value.toLowerCase() : "");
      return this.users.filter((user) => {
        return (
          safeToLower(user.first_name).includes(search) ||
          safeToLower(user.last_name).includes(search) ||
          safeToLower(user.email).includes(search) ||
          safeToLower(user.phone_number).includes(search) ||
          safeToLower(user.id_number).includes(search) ||
          safeToLower(user.user_roles?.[0]?.facility_department?.facility?.name ?? "").includes(search) ||
          safeToLower(user.user_roles?.[0]?.facility_department?.department?.name ?? "").includes(search)
        );
      });
    },
  },
  methods: {
    async fetchUsers() {
      this.loading = true;
      try {
        const response = await api.getUsers();
        const allUsers = response.data || response;
        this.users = (allUsers || []).filter(
          (u) => Array.isArray(u.user_roles) && u.user_roles.length > 0
        );
      } catch (error) {
        this.error = error.message || "Failed to fetch users";
      } finally {
        this.loading = false;
      }
    },
    applyFilters(filters) {
      this.filters = { ...this.filters, ...filters };
    },
    openCpdModal(user) {
      this.selectedUser = user;
      this.showCpdModal = true;
      this.loadCpdProgress(user.user_id);
    },
    closeCpdModal() {
      this.showCpdModal = false;
      this.selectedUser = null;
      this.cpdProgress = null;
      this.cpdError = null;
    },
    async loadCpdProgress(userId) {
      this.cpdLoading = true;
      this.cpdError = null;
      this.cpdProgress = null;
      try {
        const response = await api.getUserCpdProgress(userId);
        this.cpdProgress = response.data || response;
      } catch (error) {
        this.cpdError = error.message || 'Failed to fetch CPD progress';
      } finally {
        this.cpdLoading = false;
      }
    },
    formatDate(date) {
      if (!date) return '';
      try {
        const d = new Date(date);
        if (isNaN(d)) return date;
        return d.toLocaleDateString();
      } catch {
        return date;
      }
    },
    formatDateTime(iso) {
      try {
        return new Date(iso).toLocaleString();
      } catch {
        return iso;
      }
    }
  },
};
</script>

<style lang="css" scoped></style>


